import React, { useState, useEffect } from "react";
import { ArrowLeft, X, Save, Plus, Unlink, AlertCircle, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "react-toastify";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import ManifiestoInfoModal from "./ManifiestoInfoModal";
import PreManifiestoForm from "./PreManifiestoForm";
import { formatDateOnlyForDisplay, isFechaVencidaColombia } from "../../../../../../utils/dateUtils";
import "./PreManifiestoDetail.scss";

const PreManifiestoDetail = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  preManifiestoId,
  onBack,
  onRefresh,
  onCreateRemesa,
  onManifiestoCreated,
  isLogisticsDemo = false,
}) => {
  const [preManifiesto, setPreManifiesto] = useState(null);
  const [remesas, setRemesas] = useState([]);
  const [selectedRemesas, setSelectedRemesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [unassignedRemesas, setUnassignedRemesas] = useState([]);
  const [selectedForAssignment, setSelectedForAssignment] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showMismatchWarning, setShowMismatchWarning] = useState(false);
  const [mismatchDetails, setMismatchDetails] = useState(null);
  const [showManifiestoInfoModal, setShowManifiestoInfoModal] = useState(false);
  const [rndcProgress, setRndcProgress] = useState(null);
  const [showEditPreManifiestoModal, setShowEditPreManifiestoModal] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preManifiestoId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pmRes, remesasRes] = await Promise.all([
        roldanApi.getPreManifiestoById(mantisBusinessId, token, preManifiestoId),
        roldanApi.getRemesasByPreManifiesto(mantisBusinessId, token, preManifiestoId),
      ]);
      if (pmRes?.success) setPreManifiesto(pmRes.data);
      if (remesasRes?.success) {
        setRemesas(remesasRes.data || []);
        setSelectedRemesas(
          (remesasRes.data || [])
            .filter((r) => r.estado === "Confirmada para Carga")
            .map((r) => r.remesa_id)
        );
      }
    } catch (error) {
      console.error("Error loading pre-manifiesto:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRemesa = (remesaId) => {
    setSelectedRemesas((prev) =>
      prev.includes(remesaId) ? prev.filter((id) => id !== remesaId) : [...prev, remesaId]
    );
  };

  const loadUnassignedRemesas = async () => {
    setLoadingUnassigned(true);
    try {
      // Fetch remesas that are not assigned to any pre-manifiesto
      const res = await roldanApi.getRemesas(mantisBusinessId, token, { 
        estado_asociacion: "Sin Asignar",
        limit: 100 
      });
      if (res?.success) {
        const unassigned = res.data || [];
        // Filter out remesas already assigned to this pre-manifiesto
        const assignedIds = remesas.map(r => r.remesa_id);
        setUnassignedRemesas(unassigned.filter(r => !assignedIds.includes(r.remesa_id)));
      }
    } catch (error) {
      console.error("Error loading unassigned remesas:", error);
      alert("Error al cargar remesas no asignadas");
    } finally {
      setLoadingUnassigned(false);
    }
  };

  const handleOpenAssignmentModal = () => {
    setShowAssignmentModal(true);
    setSelectedForAssignment([]);
    loadUnassignedRemesas();
  };

  const handleToggleAssignment = (remesaId) => {
    setSelectedForAssignment((prev) =>
      prev.includes(remesaId) ? prev.filter((id) => id !== remesaId) : [...prev, remesaId]
    );
  };

  const handleAssignRemesas = async () => {
    if (selectedForAssignment.length === 0) {
      alert("Debe seleccionar al menos una remesa");
      return;
    }

    setAssigning(true);
    try {
      const res = await roldanApi.assignRemesasToPreManifiesto(
        mantisBusinessId,
        token,
        preManifiestoId,
        selectedForAssignment
      );
      if (res?.success) {
        alert("Remesas asignadas exitosamente");
        setShowAssignmentModal(false);
        setSelectedForAssignment([]);
        loadData();
        if (onRefresh) onRefresh();
      } else {
        alert("Error al asignar remesas");
      }
    } catch (error) {
      console.error("Error assigning remesas:", error);
      alert("Error al asignar remesas");
    } finally {
      setAssigning(false);
    }
  };

  const handleUnlinkRemesas = async () => {
    if (selectedRemesas.length === 0) {
      alert("Debe seleccionar al menos una remesa para desvincular");
      return;
    }

    const count = selectedRemesas.length;
    const message = count === 1 
      ? "¿Confirma desvincular esta remesa del pre-manifiesto?"
      : `¿Confirma desvincular ${count} remesas del pre-manifiesto?`;

    if (!window.confirm(message)) {
      return;
    }

    setUnlinking(true);
    try {
      const res = await roldanApi.unlinkRemesasFromPreManifiesto(
        mantisBusinessId,
        token,
        preManifiestoId,
        selectedRemesas
      );
      if (res?.success) {
        alert(count === 1 ? "Remesa desvinculada exitosamente" : "Remesas desvinculadas exitosamente");
        setSelectedRemesas([]);
        loadData();
        if (onRefresh) onRefresh();
      } else {
        alert("Error al desvincular remesas");
      }
    } catch (error) {
      console.error("Error unlinking remesas:", error);
      alert("Error al desvincular remesas");
    } finally {
      setUnlinking(false);
    }
  };

  const checkOrigenDestinoMismatch = () => {
    if (!preManifiesto || selectedRemesas.length === 0) {
      return null;
    }

    const selectedRemesasData = remesas.filter(r => selectedRemesas.includes(r.remesa_id));
    const pmOrigen = preManifiesto.origen?.trim();
    const pmDestino = preManifiesto.destino?.trim();
    const pmOrigenId = preManifiesto.origen_id?.toString().trim();
    const pmDestinoId = preManifiesto.destino_id?.toString().trim();

    const mismatches = {
      origen: [],
      destino: [],
    };

    selectedRemesasData.forEach(remesa => {
      const remesaOrigen = remesa.origen?.trim();
      const remesaDestino = remesa.destino?.trim();
      const remesaOrigenId = remesa.origen_id?.toString().trim();
      const remesaDestinoId = remesa.destino_id?.toString().trim();

      // Check origen mismatch
      // Priority: Compare IDs if both exist, otherwise compare names
      let origenMismatch = false;
      if (pmOrigenId && remesaOrigenId) {
        // Both have IDs - compare IDs
        origenMismatch = pmOrigenId !== remesaOrigenId;
      } else if (pmOrigen && remesaOrigen) {
        // Both have names - compare names (case-insensitive)
        origenMismatch = pmOrigen.toLowerCase() !== remesaOrigen.toLowerCase();
      } else if ((pmOrigenId || pmOrigen) && (remesaOrigenId || remesaOrigen)) {
        // One has value, other doesn't - consider mismatch
        origenMismatch = true;
      }

      if (origenMismatch) {
        mismatches.origen.push({
          remesa: remesa.numero_remesa,
          preManifiestoOrigen: pmOrigen || pmOrigenId || "N/A",
          remesaOrigen: remesaOrigen || remesaOrigenId || "N/A"
        });
      }

      // Check destino mismatch
      // Priority: Compare IDs if both exist, otherwise compare names
      let destinoMismatch = false;
      if (pmDestinoId && remesaDestinoId) {
        // Both have IDs - compare IDs
        destinoMismatch = pmDestinoId !== remesaDestinoId;
      } else if (pmDestino && remesaDestino) {
        // Both have names - compare names (case-insensitive)
        destinoMismatch = pmDestino.toLowerCase() !== remesaDestino.toLowerCase();
      } else if ((pmDestinoId || pmDestino) && (remesaDestinoId || remesaDestino)) {
        // One has value, other doesn't - consider mismatch
        destinoMismatch = true;
      }

      if (destinoMismatch) {
        mismatches.destino.push({
          remesa: remesa.numero_remesa,
          preManifiestoDestino: pmDestino || pmDestinoId || "N/A",
          remesaDestino: remesaDestino || remesaDestinoId || "N/A"
        });
      }
    });

    if (mismatches.origen.length > 0 || mismatches.destino.length > 0) {
      return mismatches;
    }

    return null;
  };

  const handleGenerarManifiesto = async () => {
    if (selectedRemesas.length === 0) {
      alert("Debe seleccionar al menos una remesa");
      return;
    }

    // Check for origen/destino mismatches
    const mismatch = checkOrigenDestinoMismatch();
    if (mismatch) {
      setMismatchDetails({
        preManifiestoOrigen: preManifiesto.origen || preManifiesto.origen_id || "N/A",
        preManifiestoDestino: preManifiesto.destino || preManifiesto.destino_id || "N/A",
        origenMismatches: mismatch.origen,
        destinoMismatches: mismatch.destino
      });
      setShowMismatchWarning(true);
      return;
    }

    // If no mismatch, open the ManifiestoInfoModal to collect FRONTENDINFO
    setShowManifiestoInfoModal(true);
  };

  const handleManifiestoInfoSubmit = async (frontendInfo) => {
    setGenerating(true);
    setShowMismatchWarning(false);
    setRndcProgress({ steps: [], currentStep: 0, completedSteps: [], error: null });
    try {
      // Demo logística: no consultar tarifa mínima RNDC (evita llamada al ministerio)
      if (!isLogisticsDemo) {
        const validateRes = await roldanApi.validateRndcTarifa(mantisBusinessId, token, {
          origen_id: frontendInfo.municipioOrigen,
          destino_id: frontendInfo.municipioDestino,
          flete: frontendInfo.valorFlete,
        });
        if (validateRes?.success === false && validateRes?.rndc_error) {
          const userConfirmed = window.confirm(
            `No se pudo validar la tarifa RNDC:\n\n${validateRes.rndc_error}\n\n¿Desea continuar y generar el manifiesto de todas formas?`
          );
          if (!userConfirmed) {
            setGenerating(false);
            return;
          }
        } else if (validateRes?.warning && validateRes?.message) {
          const userConfirmed = window.confirm(
            `${validateRes.message}\n\n¿Desea continuar y generar el manifiesto de todas formas?`
          );
          if (!userConfirmed) {
            setGenerating(false);
            return;
          }
        }
      }

      const payload = {
        pre_manifiesto_id: preManifiestoId,
        remesaIds: selectedRemesas,
        frontendInfo,
        ...(isLogisticsDemo ? { skipMinisterioRndc: true } : {}),
      };

      const startRes = await roldanApi.createManifiestoWithRNDCStart(
        mantisBusinessId,
        mantisBusinessUserId,
        token,
        payload
      );

      if (!startRes?.success) {
        const errMsg = startRes?.message || startRes?.error || "Error al iniciar la creación del manifiesto";
        setRndcProgress((p) => (p ? { ...p, error: errMsg } : null));
        toast.error(errMsg);
        return;
      }

      const { jobId, steps, completedSteps, firstStepIndex } = startRes;
      const startFrom = firstStepIndex ?? 1;
      const isResuming = startFrom > 1;
      setRndcProgress({
        steps: steps || [],
        currentStep: completedSteps?.[completedSteps.length - 1] ?? 0,
        completedSteps: completedSteps || [0],
        error: null,
        resumingFrom: isResuming ? startFrom : undefined,
      });

      let lastRes = startRes;
      for (let stepIndex = startFrom; stepIndex <= 6; stepIndex++) {
        const stepRes = await roldanApi.createManifiestoWithRNDCStep(
          mantisBusinessId,
          token,
          jobId,
          stepIndex
        );
        lastRes = stepRes;

        if (!stepRes?.success) {
          const errMsg = stepRes?.message || stepRes?.error || "Error en el paso de creación";
          setRndcProgress((p) => (p ? { ...p, error: errMsg, currentStep: stepIndex } : null));
          toast.error(errMsg);
          return;
        }

        setRndcProgress({
          steps: stepRes.steps || steps || [],
          currentStep: stepIndex,
          completedSteps: stepRes.completedSteps || Array.from({ length: stepIndex + 1 }, (_, i) => i),
          error: null,
          resumingFrom: undefined,
        });

        if (stepRes.done && stepRes.data) {
          toast.success(
            isLogisticsDemo
              ? "Manifiesto generado (demo: sin registro RNDC Ministerio). Puede descargar el PDF."
              : "Manifiesto creado exitosamente con RNDC"
          );
          setRndcProgress(null);
          setShowManifiestoInfoModal(false);
          if (onRefresh) onRefresh();

          const data = stepRes.data;
          const manifiestoId = data.manifiesto_id || data._id || data.id;
          if (manifiestoId && onManifiestoCreated) {
            onManifiestoCreated(manifiestoId);
          } else {
            onBack();
          }

          if (data.ingresoid || data.seguridadqr) {
            const summary = [];
            if (data.ingresoid) summary.push(`Ingreso ID: ${data.ingresoid}`);
            if (data.seguridadqr) summary.push(`Seguridad QR: ${data.seguridadqr}`);
            if (summary.length > 0) toast.info(summary.join(" | "), { autoClose: 5000 });
          }
          return;
        }
      }
    } catch (error) {
      console.error("Error creating manifiesto with RNDC:", error);
      const errMsg = error?.message || error?.response?.data?.message || "Error al generar el manifiesto";
      setRndcProgress((p) => (p ? { ...p, error: errMsg } : null));
      toast.error(errMsg);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando...</div>;
  if (!preManifiesto) return <div className="p-8 text-center text-red-600">Pre-manifiesto no encontrado</div>;

  const vencido = isFechaVencidaColombia(preManifiesto.fecha_viaje);
  const isManifestado = (preManifiesto.estado || '').toLowerCase().includes('manifestado');
  const showVencido = vencido && !isManifestado;
  const canEditPreManifiesto = !isManifestado;

  return (
    <div className="px-2">
      {/* Mismatch Warning Modal */}
      {showMismatchWarning && mismatchDetails && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowMismatchWarning(false)}
        >
          <div
            className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-amber-50">
              <AlertTriangle size={24} className="text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900">Advertencia: Origen/Destino no coinciden</h2>
                <p className="text-sm text-gray-700 mt-1">
                  Las remesas seleccionadas tienen origen o destino diferentes al pre-manifiesto
                </p>
              </div>
              <button
                onClick={() => setShowMismatchWarning(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Pre-Manifiesto:</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Origen:</span>{" "}
                      <span className="font-medium">{mismatchDetails.preManifiestoOrigen}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Destino:</span>{" "}
                      <span className="font-medium">{mismatchDetails.preManifiestoDestino}</span>
                    </div>
                  </div>
                </div>

                {mismatchDetails.origenMismatches.length > 0 && (
                  <div className="border-l-4 border-red-500 bg-red-50 rounded p-4">
                    <h3 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Origen no coincide ({mismatchDetails.origenMismatches.length} remesa{mismatchDetails.origenMismatches.length > 1 ? 's' : ''})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {mismatchDetails.origenMismatches.map((mismatch, idx) => (
                        <li key={idx} className="text-red-800">
                          <span className="font-mono font-semibold">{mismatch.remesa}</span>:{" "}
                          <span className="font-medium">{mismatch.remesaOrigen}</span> (Pre-Manifiesto: {mismatch.preManifiestoOrigen})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {mismatchDetails.destinoMismatches.length > 0 && (
                  <div className="border-l-4 border-red-500 bg-red-50 rounded p-4">
                    <h3 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Destino no coincide ({mismatchDetails.destinoMismatches.length} remesa{mismatchDetails.destinoMismatches.length > 1 ? 's' : ''})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {mismatchDetails.destinoMismatches.map((mismatch, idx) => (
                        <li key={idx} className="text-red-800">
                          <span className="font-mono font-semibold">{mismatch.remesa}</span>:{" "}
                          <span className="font-medium">{mismatch.remesaDestino}</span> (Pre-Manifiesto: {mismatch.preManifiestoDestino})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Nota:</strong> Puede proceder con la creación del manifiesto, pero esto puede causar inconsistencias en los datos.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowMismatchWarning(false)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-100 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowMismatchWarning(false);
                  setShowManifiestoInfoModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg"
              >
                <AlertTriangle size={16} />
                Proceder de todas formas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pre-Manifiesto (fecha, origen, destino) - only when not Manifestado */}
      {showEditPreManifiestoModal && (
        <PreManifiestoForm
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={mantisBusinessUserId}
          token={token}
          preManifiestoId={preManifiestoId}
          hideEstado
          onClose={() => setShowEditPreManifiestoModal(false)}
          onSuccess={() => {
            setShowEditPreManifiestoModal(false);
            loadData();
            if (onRefresh) onRefresh();
            toast.success("Pre-manifiesto actualizado");
          }}
        />
      )}

      {/* Manifiesto Info Modal (RNDC) */}
      {showManifiestoInfoModal && preManifiesto && (
        <ManifiestoInfoModal
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={mantisBusinessUserId}
          token={token}
          preManifiesto={preManifiesto}
          remesas={remesas}
          selectedRemesaIds={selectedRemesas}
          onClose={() => {
            if (!generating) {
              setShowManifiestoInfoModal(false);
              setRndcProgress(null);
            }
          }}
          onSubmit={handleManifiestoInfoSubmit}
          submitting={generating}
          progress={rndcProgress}
          isLogisticsDemo={isLogisticsDemo}
        />
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowAssignmentModal(false)}
        >
          <div
            className="w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">Agregar Remesas al Pre-Manifiesto</h2>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {loadingUnassigned ? (
                <div className="p-8 text-center text-gray-500">Cargando remesas disponibles...</div>
              ) : unassignedRemesas.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No hay remesas disponibles para asignar</div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Selecciona una o más remesas para asignar a este pre-manifiesto
                  </p>
                  <div className="grid gap-2 max-h-[60vh] overflow-y-auto">
                    {unassignedRemesas.map((remesa) => (
                      <label
                        key={remesa.remesa_id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                          selectedForAssignment.includes(remesa.remesa_id)
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-200 bg-white hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={selectedForAssignment.includes(remesa.remesa_id)}
                          onChange={() => handleToggleAssignment(remesa.remesa_id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-semibold text-gray-900">{remesa.numero_remesa}</span>
                            <span className="text-xs font-semibold inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                              {remesa.estado}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">
                            <p>{remesa.cliente || "N/A"}</p>
                            {remesa.nombre_conductor && (
                              <p className="text-xs text-gray-600">Conductor: {remesa.nombre_conductor}</p>
                            )}
                            {remesa.origen && <p className="text-xs text-gray-600">Origen: {remesa.origen}</p>}
                            {remesa.destino && <p className="text-xs text-gray-600">Destino: {remesa.destino}</p>}
                            {remesa.placa_vehiculo && <p className="text-xs text-gray-600">Placa: {remesa.placa_vehiculo}</p>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowAssignmentModal(false)}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAssignRemesas}
                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                disabled={selectedForAssignment.length === 0 || assigning}
              >
                <Plus size={16} /> {assigning ? "Asignando..." : `Asignar (${selectedForAssignment.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm pre-manifiesto-detail-container">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 border-b border-gray-100">
          <button onClick={onBack} className="inline-flex items-center justify-center sm:justify-start gap-2 text-sm text-gray-700 hover:text-gray-900 w-full sm:w-auto">
            <ArrowLeft size={18} /> Volver
          </button>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            {onCreateRemesa && (
              <button 
                onClick={() => onCreateRemesa()}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg w-full sm:w-auto"
              >
                <Plus size={16} /> <span className="whitespace-nowrap">Nueva Remesa Rápida</span>
              </button>
            )}
            <button onClick={handleOpenAssignmentModal} className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg w-full sm:w-auto">
              <Plus size={16} /> <span className="whitespace-nowrap">Agregar Remesas</span>
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h1 className="font-mono font-extrabold text-gray-900 text-lg">{preManifiesto.numero_pre_manifiesto}</h1>
              {showVencido && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-xs bg-red-500 text-white">
                  <AlertCircle size={12} /> Vencido
                </span>
              )}
            </div>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-semibold text-xs ${
              (preManifiesto.estado || '').toLowerCase().includes('abierto') ? 'bg-sky-100 text-sky-800' :
              (preManifiesto.estado || '').toLowerCase().includes('manifestado') ? 'bg-violet-100 text-violet-800' :
              'bg-gray-100 text-gray-700'
            }`}>{preManifiesto.estado}</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-800">
              <p className={showVencido ? 'text-red-700 font-semibold' : ''}>
                <span className="text-gray-500 font-semibold">Fecha Viaje:</span> {formatDateOnlyForDisplay(preManifiesto.fecha_viaje)}
              </p>
              {preManifiesto.origen && (
                <p><span className="text-gray-500 font-semibold">Origen:</span> {preManifiesto.origen}</p>
              )}
              {preManifiesto.destino && (
                <p><span className="text-gray-500 font-semibold">Destino:</span> {preManifiesto.destino}</p>
              )}
            </div>
            {canEditPreManifiesto && (
              <button
                type="button"
                onClick={() => setShowEditPreManifiestoModal(true)}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium whitespace-nowrap self-start sm:self-center"
              >
                <Pencil size={16} /> Editar fecha, origen y destino
              </button>
            )}
          </div>

          {remesas.length > 0 && (
            <h2 className="text-base font-bold text-gray-900 mb-2">Remesas del Pre-Manifiesto</h2>
          )}
          <div className="grid gap-2 mb-4">
            {remesas.map((remesa) => (
              <label key={remesa.remesa_id} className={`flex items-center gap-3 p-3 border rounded-lg ${selectedRemesas.includes(remesa.remesa_id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'} ${remesa.estado === "Manifestada" ? 'opacity-60' : ''}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={selectedRemesas.includes(remesa.remesa_id)}
                  onChange={() => handleToggleRemesa(remesa.remesa_id)}
                />
                <div className="flex flex-col">
                  <span className="font-mono font-semibold text-gray-900">{remesa.numero_remesa}</span>
                  <span className="text-gray-700">{remesa.cliente || "N/A"}</span>
                  {remesa.nombre_conductor && (
                    <span className="text-xs text-gray-600">Conductor: {remesa.nombre_conductor}</span>
                  )}
                  {remesa.origen && (
                    <span className="text-xs text-gray-600">Origen: {remesa.origen}</span>
                  )}
                  {remesa.destino && (
                    <span className="text-xs text-gray-600">Destino: {remesa.destino}</span>
                  )}
                  {remesa.placa_vehiculo && (
                    <span className="text-xs text-gray-600">Placa: {remesa.placa_vehiculo}</span>
                  )}
                  {remesa.peso_mercancia_kg && (
                    <span className="text-xs text-gray-600">Peso: {remesa.peso_mercancia_kg} kg</span>
                  )}
                  <span className="text-xs font-semibold inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 w-max mt-1">{remesa.estado}</span>
                </div>
              </label>
            ))}
          </div>

          <div className="pre-manifiesto-actions flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-3 border-t border-dashed border-gray-200">
            <button
              onClick={handleUnlinkRemesas}
              className="pre-manifiesto-action-btn inline-flex items-center justify-center gap-2 px-3 py-2 border border-red-200 text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 text-sm font-medium whitespace-nowrap"
              disabled={selectedRemesas.length === 0 || unlinking}
              title="Desvincular remesas seleccionadas del pre-manifiesto"
            >
              <Unlink size={16} /> <span className="action-text">{unlinking ? "Desvinculando..." : "Desvincular"}</span> <span className="action-count">({selectedRemesas.length})</span>
            </button>
            <button
              onClick={handleGenerarManifiesto}
              className="pre-manifiesto-action-btn inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 whitespace-nowrap"
              disabled={selectedRemesas.length === 0 || generating}
            >
              <Save size={16} /> <span className="action-text">{generating ? "Generando..." : "Generar Manifiesto"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreManifiestoDetail;

